import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { apiUrl } from "../apiConfig.js";
import logo from "../logo.JPG";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if the user is already logged in and if the token is valid
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp < currentTime) {
          sessionStorage.removeItem("token");
          navigate("/login");
        } else {
          navigate("/catalogo/conceptos");
        }
      } catch (error) {
        console.error("Token decoding failed:", error);
        sessionStorage.removeItem("token");
        navigate("/login");
      }
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    if (!username || !password) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await fetch(apiUrl("api/v1/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const jwt = await response.text();
        sessionStorage.setItem("token", jwt);
        navigate("/");
      } else {
        setError("Usuario o contraseña incorrectos.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Ocurrió un error al intentar iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fafafa",
      }}
    >
      <Container maxWidth="xs">
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <img
            src={logo}
            alt="FinSmart"
            style={{
              height: 64,
              width: 64,
              objectFit: "contain",
              borderRadius: 8,
              marginBottom: 12,
            }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "grey.800", letterSpacing: "-0.02em" }}
          >
            FinSmart
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
            p: 4,
          }}
        >
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ mb: 3, fontWeight: 600, color: "grey.700" }}
          >
            Iniciar Sesión
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Usuario"
                variant="outlined"
                fullWidth
                size="small"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />

              <TextField
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                size="small"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                    Iniciando sesión...
                  </>
                ) : (
                  "Iniciar Sesión"
                )}
              </Button>

              <Typography variant="body2" align="center" sx={{ mt: 1, color: "grey.600" }}>
                <RouterLink
                  to="/register"
                  style={{
                    color: "#1976d2",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  Crear cuenta
                </RouterLink>
              </Typography>
            </Box>
          </form>
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          align="center"
          display="block"
          sx={{ mt: 4, color: "grey.400" }}
        >
          &copy; FinSmart
        </Typography>
      </Container>
    </Box>
  );
}

export default Login;
